import { regions, years, sortOptions } from "@/mocks/videos";

interface FilterBarProps {
  subcategories: string[];
  activeSub: string;
  onSubChange: (value: string) => void;
  region: string;
  onRegionChange: (value: string) => void;
  year: string;
  onYearChange: (value: string) => void;
  sort: string;
  onSortChange: (value: string) => void;
  onReset: () => void;
}

export default function FilterBar({
  subcategories,
  activeSub,
  onSubChange,
  region,
  onRegionChange,
  year,
  onYearChange,
  sort,
  onSortChange,
  onReset,
}: FilterBarProps) {
  const chipClass = (active: boolean) =>
    `px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
      active
        ? "bg-primary-500 text-background-50"
        : "bg-background-100 text-foreground-600 hover:text-primary-600"
    }`;

  const selectClass =
    "w-full px-3 py-2.5 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-800 focus:outline-none focus:border-primary-400";

  const Row = ({
    label,
    options,
    value,
    onChange,
  }: {
    label: string;
    options: string[];
    value: string;
    onChange: (v: string) => void;
  }) => (
    <div className="hidden md:flex items-start gap-4 py-2.5 border-b border-background-200 last:border-b-0">
      <span className="text-sm text-foreground-400 shrink-0 pt-1.5 w-12">{label}</span>
      <div className="flex items-center gap-2 flex-wrap">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={chipClass(value === opt)}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="rounded-lg bg-background-50 border border-background-200 px-4 md:px-5 py-3 md:py-2 mb-5">
      {/* Mobile selects */}
      <div className="md:hidden grid grid-cols-2 gap-3 py-1">
        {subcategories.length > 0 && (
          <select
            value={activeSub}
            onChange={(e) => onSubChange(e.target.value)}
            className={selectClass}
          >
            <option value="全部">全部类型</option>
            {subcategories.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        )}
        <select value={region} onChange={(e) => onRegionChange(e.target.value)} className={selectClass}>
          {regions.map((r) => (
            <option key={r} value={r}>
              {r === "全部" ? "全部地区" : r}
            </option>
          ))}
        </select>
        <select value={year} onChange={(e) => onYearChange(e.target.value)} className={selectClass}>
          {years.map((y) => (
            <option key={y} value={y}>
              {y === "全部" ? "全部年份" : `${y}年`}
            </option>
          ))}
        </select>
        <select value={sort} onChange={(e) => onSortChange(e.target.value)} className={selectClass}>
          {sortOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={onReset}
          className="col-span-2 flex items-center justify-center gap-1 py-2.5 rounded-md border border-background-300 text-sm text-foreground-600 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>
          重置筛选
        </button>
      </div>

      {/* Desktop rows */}
      <div className="hidden md:block">
        {subcategories.length > 0 && (
          <Row label="类型" options={["全部", ...subcategories]} value={activeSub} onChange={onSubChange} />
        )}
        <Row label="地区" options={regions} value={region} onChange={onRegionChange} />
        <Row label="年份" options={years} value={year} onChange={onYearChange} />
        <Row label="排序" options={sortOptions} value={sort} onChange={onSortChange} />
        <div className="flex justify-end pt-3">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}